import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import PageHeader from "@/components/PageHeader";
import JobCard from "@/components/JobCard";
import Autocomplete from "@/components/Autocomplete";
import { Reveal } from "@/components/Section";
import api, { trackEvent } from "@/lib/api";

const POSTED = [["", "Any time"], ["24h", "Last 24 hours"], ["7d", "Last 7 days"], ["30d", "Last 30 days"]];

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [meta, setMeta] = useState({ locations: [], industries: [], categories: [], employment_types: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const [loc, setLoc] = useState(params.get("location") || "");
  const page = parseInt(params.get("page") || "1");

  const get = (k) => params.get(k) || "";
  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    p.set("page", "1");
    setParams(p);
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams(params);
    p.set("limit", "12");
    try {
      const { data } = await api.get(`/jobs?${p.toString()}`);
      setData(data);
    } catch (e) {} finally { setLoading(false); }
  }, [params]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { api.get("/jobs/filters/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const applySearch = (e) => {
    e?.preventDefault();
    trackEvent("job_search", { q, location: loc });
    const p = new URLSearchParams(params);
    q ? p.set("q", q) : p.delete("q");
    loc ? p.set("location", loc) : p.delete("location");
    p.set("page", "1");
    setParams(p);
  };

  const clearAll = () => { setQ(""); setLoc(""); setParams(new URLSearchParams()); };
  const totalPages = Math.ceil(data.total / 12);

  const FilterSelect = ({ label, k, options }) => (
    <div>
      <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>
      <Select value={get(k) || "all"} onValueChange={(v) => setParam(k, v === "all" ? "" : v)}>
        <SelectTrigger data-testid={`filter-${k}`} className="bg-white/5 border-white/10"><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent className="glass-strong border-white/10">
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <PublicLayout waMessage="Hi Dreamzz HR & Consultancy, I am looking for job opportunities.">
      <Seo title="Jobs in Karnataka" description="Browse the latest job openings across Karnataka — hospitality, driver, security, sales, accounts and more. Apply with Dreamzz HR & Consultancy." />
      <PageHeader eyebrow="Careers" title="Find Your Next Opportunity" subtitle={`${data.total} genuine openings across Karnataka. Search, filter and apply in minutes.`}>
        <form onSubmit={applySearch} className="mt-6 grid gap-3 sm:grid-cols-[1fr_1fr_auto] max-w-3xl">
          <Autocomplete
            testId="jobs-search-keyword" field="title" value={q} onChange={setQ}
            placeholder="Job title, skill or keyword"
            icon={<Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />}
          />
          <Autocomplete
            testId="jobs-search-location" field="location" value={loc} onChange={setLoc}
            placeholder="City / location"
            icon={<MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />}
          />
          <Button data-testid="jobs-search-submit" type="submit" className="h-12 bg-brand-emerald hover:bg-[#C62828] text-white font-semibold px-8">Search</Button>
        </form>
      </PageHeader>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
          <div className="glass rounded-2xl p-5 sticky top-24 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold flex items-center gap-2"><SlidersHorizontal size={18} className="text-brand-emerald" />Filters</h3>
              <button onClick={clearAll} data-testid="jobs-clear-filters" className="text-xs text-slate-400 hover:text-brand-emerald flex items-center gap-1"><X size={14} />Clear</button>
            </div>
            <FilterSelect label="Location" k="location" options={meta.locations} />
            <FilterSelect label="Industry" k="industry" options={meta.industries} />
            <FilterSelect label="Category" k="category" options={meta.categories} />
            <FilterSelect label="Employment Type" k="employment_type" options={meta.employment_types} />
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Posted Date</label>
              <Select value={get("posted") || ""} onValueChange={(v) => setParam("posted", v)}>
                <SelectTrigger data-testid="filter-posted" className="bg-white/5 border-white/10"><SelectValue placeholder="Any time" /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {POSTED.map(([v, l]) => <SelectItem key={v || "any"} value={v || "any"}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-400">{loading ? "Loading..." : `Showing ${data.items.length} of ${data.total} jobs`}</p>
            <Button variant="outline" size="sm" className="lg:hidden border-white/15" onClick={() => setShowFilters((s) => !s)} data-testid="jobs-toggle-filters">
              <SlidersHorizontal size={16} className="mr-2" />Filters
            </Button>
          </div>
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="glass rounded-2xl h-48 animate-pulse" />)}</div>
          ) : data.items.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center text-slate-400" data-testid="jobs-empty">No jobs found. Try adjusting your search or filters.</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.items.map((j, i) => <Reveal key={j.id} delay={i * 30}><JobCard job={j} /></Reveal>)}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button key={i} data-testid={`jobs-page-${i + 1}`} onClick={() => setParam("page", String(i + 1))}
                  className={`h-10 w-10 rounded-lg font-medium ${page === i + 1 ? "bg-brand-emerald text-white" : "glass text-slate-300"}`}>{i + 1}</button>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
